import { slugify } from './slugify';

/**
 * Returns the correct internal route for a procedure based on its CollectionSource.
 * Uses SEO-friendly URLs: /{section}/{slug}/{mongoId}
 *
 * ADV                                    → /achizitii-directe/{slug}/{mongoId}
 * CN, SCN, RFQ, DC, PC, RFD             → /licitatii/{slug}/{mongoId}
 * CAN, SCNA, RFDA, RFQA, PCA, RDC       → /anunturi-atribuire/{slug}/{mongoId}
 * PI                                     → /anunturi-intentie/{slug}/{mongoId}
 *
 * @param {string} mongoId - The MongoDB ObjectId string for the procedure
 * @param {string} collectionSource - The collection source type
 * @param {string} [title] - The auction title (used to generate the URL slug)
 */
export const getProcedureLink = (mongoId, collectionSource, title) => {
  if (!mongoId || !collectionSource) return null;

  const slug = slugify(title);
  const src = collectionSource.toUpperCase();

  if (src.includes('_ADV')) {
    return `/achizitii-directe/${slug}/${mongoId}`;
  }

  if (src.includes('_PI')) {
    return `/anunturi-intentie/${slug}/${mongoId}`;
  }

  const awardSuffixes = ['_CAN', '_SCNA', '_RFDA', '_RFQA', '_PCA', '_RDC'];
  if (awardSuffixes.some((s) => src.includes(s))) {
    return `/anunturi-atribuire/${slug}/${mongoId}`;
  }

  const participationSuffixes = ['_CN', '_SCN', '_RFQ', '_DC', '_PC', '_RFD'];
  if (participationSuffixes.some((s) => src.includes(s))) {
    return `/licitatii/${slug}/${mongoId}`;
  }

  return null;
};

/**
 * Returns a human-readable label for the procedure type.
 */
export const getProcedureTypeLabel = (collectionSource) => {
  if (!collectionSource) return null;

  const src = collectionSource.toUpperCase();

  if (src === 'PAAP') return 'Plan Achiziții';

  if (src.includes('_ADV')) return 'Achiziție directă';
  if (src.includes('_PI')) return 'Anunț de intenție';

  const awardSuffixes = ['_CAN', '_SCNA', '_RFDA', '_RFQA', '_PCA', '_RDC'];
  if (awardSuffixes.some((s) => src.includes(s))) return 'Atribuire';

  const participationSuffixes = ['_CN', '_SCN', '_RFQ', '_DC', '_PC', '_RFD'];
  if (participationSuffixes.some((s) => src.includes(s))) return 'Procedură';

  return null;
};

// Friendly Romanian labels for each SEAP collection type. Keep in sync with
// the Collections_* family in backend/Mongo. Used to turn raw values like
// "Collections_SCN:ExactMatch" into "SEAP — Anunț simplificat" instead of
// leaking internal jargon to the UI.
const SEAP_TYPE_LABELS = {
  CN: 'Anunț de participare',
  SCN: 'Anunț simplificat',
  RFQ: 'Cerere de ofertă',
  DC: 'Concurs de soluții',
  PC: 'Concesiune',
  RFD: 'Cerere documentație',
  CAN: 'Anunț de atribuire',
  SCNA: 'Atribuire simplificată',
  RFDA: 'Atribuire RFD',
  RFQA: 'Atribuire RFQ',
  PCA: 'Atribuire concesiune',
  RDC: 'Atribuire concurs',
  ADV: 'Achiziție directă',
  PI: 'Anunț de intenție',
};

export const getProjectSourceLabel = (source) => {
  if (!source) return null;

  // Strip any ":<MatchType>" suffix (FuzzyMatch / ExactMatch / etc) — internal
  // grouping detail, not user-facing.
  const base = source.split(':')[0];

  if (base === 'PAAP') return 'Plan Achiziții Publice';
  if (base === 'Procedures') return 'Proceduri SEAP';

  if (base.startsWith('Collections_')) {
    const typeKey = base.replace('Collections_', '');
    return SEAP_TYPE_LABELS[typeKey]
      ? `SEAP — ${SEAP_TYPE_LABELS[typeKey]}`
      : 'SEAP';
  }

  return base;
};
